import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { adminAPI } from '../utils/api';
import './AdminDashboard.css';

const STATUS_COLORS = { PENDING:'warning', CONFIRMED:'info', IN_PROGRESS:'info', COMPLETED:'success', CANCELLED:'error' };

export default function AdminDashboard() {
  const [tab, setTab] = useState('dashboard');
  const [dashData, setDashData] = useState(null);
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userSearch, setUserSearch] = useState('');
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const { data } = await adminAPI.getDashboard();
      setDashData(data);
    } catch (err) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const { data } = await adminAPI.getUsers({ search: userSearch, role: userRole });
      setUsers(data.users);
    } catch { toast.error('Failed to load users'); }
  };

  const loadOrders = async () => {
    try {
      const { data } = await adminAPI.getOrders();
      setOrders(data.orders);
    } catch { toast.error('Failed to load orders'); }
  };

  useEffect(() => {
    if (tab === 'users') loadUsers();
    if (tab === 'orders') loadOrders();
  }, [tab, userSearch, userRole]);

  const toggleUserActive = async (id, current) => {
    try {
      await adminAPI.updateUser(id, { isActive: !current });
      setUsers(u => u.map(usr => usr.id === id ? { ...usr, isActive: !current } : usr));
      toast.success(current ? 'User deactivated' : 'User activated');
    } catch { toast.error('Failed to update user'); }
  };

  if (loading) return <div className="loading-page"><div className="spinner" /></div>;

  return (
    <div className="admin-page page">
      <div className="container">
        <div className="admin-header">
          <h1>⚙️ Admin Dashboard</h1>
          <span className="badge badge-error">Admin</span>
        </div>

        <div className="tabs">
          {['dashboard', 'users', 'orders'].map(t => (
            <button key={t} className={`tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* Dashboard Tab */}
        {tab === 'dashboard' && dashData && (
          <div>
            <div className="admin-stats">
              {[
                { label: 'Total Users', value: dashData.stats.totalUsers, icon: '👥', color: '#3B82F6' },
                { label: 'Sellers', value: dashData.stats.totalSellers, icon: '🏪', color: '#059669' },
                { label: 'Active Services', value: dashData.stats.totalServices, icon: '📦', color: '#D97706' },
                { label: 'Total Orders', value: dashData.stats.totalOrders, icon: '🛒', color: '#7C3AED' },
                { label: 'Platform Revenue', value: `$${dashData.stats.totalRevenue?.toFixed(2)}`, icon: '💰', color: '#DC2626' },
              ].map(s => (
                <div key={s.label} className="admin-stat-card" style={{ borderTop: `4px solid ${s.color}` }}>
                  <div className="admin-stat-icon">{s.icon}</div>
                  <div className="admin-stat-value">{s.value}</div>
                  <div className="admin-stat-label">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="admin-grid">
              <div>
                <h3 className="mb-2">Recent Orders</h3>
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th>Order</th><th>Buyer</th><th>Seller</th><th>Service</th><th>Amount</th><th>Status</th></tr></thead>
                    <tbody>
                      {dashData.recentOrders.map(o => (
                        <tr key={o.id}>
                          <td className="text-sm text-light">#{o.id.slice(-6).toUpperCase()}</td>
                          <td className="text-sm">{o.buyer.firstName} {o.buyer.lastName}</td>
                          <td className="text-sm">{o.seller.businessName || `${o.seller.firstName} ${o.seller.lastName}`}</td>
                          <td className="text-sm">{o.service.title}</td>
                          <td className="font-semibold">${o.totalAmount?.toFixed(2)}</td>
                          <td><span className={`badge badge-${STATUS_COLORS[o.status]}`}>{o.status}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h3 className="mb-2">Recent Users</h3>
                <div className="table-wrapper">
                  <table>
                    <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Joined</th></tr></thead>
                    <tbody>
                      {dashData.recentUsers.map(u => (
                        <tr key={u.id}>
                          <td className="font-semibold text-sm">{u.firstName} {u.lastName}</td>
                          <td className="text-sm text-light">{u.email}</td>
                          <td><span className="badge badge-primary">{u.role}</span></td>
                          <td className="text-sm text-light">{new Date(u.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {tab === 'users' && (
          <div>
            <div className="admin-filter-bar">
              <input type="text" className="form-input" placeholder="Search users..." value={userSearch}
                onChange={e => setUserSearch(e.target.value)} style={{ maxWidth: 300 }} />
              <select className="form-select" value={userRole} onChange={e => setUserRole(e.target.value)} style={{ width: 180 }}>
                <option value="">All Roles</option>
                <option value="BUYER">Buyers</option>
                <option value="SELLER">Sellers</option>
                <option value="ADMIN">Admins</option>
              </select>
            </div>
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Services</th><th>Status</th><th>Joined</th><th>Actions</th></tr></thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td className="font-semibold">{u.firstName} {u.lastName}</td>
                      <td className="text-sm text-light">{u.email}</td>
                      <td><span className="badge badge-primary">{u.role}</span></td>
                      <td>{u._count.services}</td>
                      <td><span className={`badge badge-${u.isActive ? 'success' : 'error'}`}>{u.isActive ? 'Active' : 'Inactive'}</span></td>
                      <td className="text-sm text-light">{new Date(u.createdAt).toLocaleDateString()}</td>
                      <td>
                        <button className={`btn btn-sm ${u.isActive ? 'btn-danger' : 'btn-secondary'}`}
                          onClick={() => toggleUserActive(u.id, u.isActive)}>
                          {u.isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Orders Tab */}
        {tab === 'orders' && (
          <div>
            <h3 className="mb-2">All Orders ({orders.length})</h3>
            <div className="table-wrapper">
              <table>
                <thead><tr><th>Order ID</th><th>Buyer</th><th>Seller</th><th>Service</th><th>Amount</th><th>Platform Fee</th><th>Status</th><th>Date</th></tr></thead>
                <tbody>
                  {orders.map(o => (
                    <tr key={o.id}>
                      <td className="text-sm font-semibold text-primary">#{o.id.slice(-8).toUpperCase()}</td>
                      <td className="text-sm">{o.buyer.firstName} {o.buyer.lastName}</td>
                      <td className="text-sm">{o.seller.businessName || `${o.seller.firstName} ${o.seller.lastName}`}</td>
                      <td className="text-sm">{o.service.title}</td>
                      <td className="font-semibold">${o.totalAmount?.toFixed(2)}</td>
                      <td className="text-success font-semibold">${o.platformFee?.toFixed(2)}</td>
                      <td><span className={`badge badge-${STATUS_COLORS[o.status]}`}>{o.status}</span></td>
                      <td className="text-sm text-light">{new Date(o.createdAt).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
